import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { companyApi } from 'shared/services/companyApi';
import { CompanyThemeSettings } from 'shared/types';
import { ApiResponse } from 'shared/services/apiClient';
import { queryKeys } from '../queryConfig';

/**
 * Hook to fetch company theme settings (authenticated)
 *
 * @param companyId - Company ID
 * @param enabled - Whether the query should run (default true)
 * @returns React Query result with theme settings
 */
export const useCompanyTheme = (
  companyId: string,
  enabled = true
): UseQueryResult<ApiResponse<{ theme: CompanyThemeSettings }>, Error> => {
  return useQuery({
    queryKey: queryKeys.companyTheme.theme(companyId),
    queryFn: async () => {
      const response = await companyApi.getTheme(companyId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch company theme');
      }

      return response;
    },
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000,        // 5 minutes
    gcTime: 10 * 60 * 1000,          // 10 minutes
    retry: 1,
  });
};

/**
 * Hook to fetch company theme settings (public, for login page)
 *
 * @param companyId - Company ID
 * @param enabled - Whether the query should run (default true)
 * @returns React Query result with theme settings
 */
export const useCompanyThemePublic = (
  companyId: string,
  enabled = true
): UseQueryResult<ApiResponse<{ theme: CompanyThemeSettings }>, Error> => {
  return useQuery({
    queryKey: queryKeys.companyTheme.themePublic(companyId),
    queryFn: async () => {
      const response = await companyApi.getThemePublic(companyId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch company theme');
      }

      return response;
    },
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000,        // 5 minutes
    gcTime: 10 * 60 * 1000,          // 10 minutes
    retry: 1,
  });
};

/**
 * Hook to fetch the presigned company logo URL.
 *
 * Combines `getTheme` (to check whether a logo is configured) and
 * `getLogoDownloadUrl` (to mint a presigned S3 URL) into a single React
 * Query entry so multiple consumers (Sidebar, CompanyDetail, etc.) share
 * a 5-minute cache instead of re-firing on every route change.
 */
export const useCompanyLogoUrl = (
  companyId: string | null | undefined,
  enabled = true,
): UseQueryResult<string | null, Error> => {
  return useQuery({
    queryKey: queryKeys.companyTheme.logoUrl(companyId || ''),
    queryFn: async (): Promise<string | null> => {
      if (!companyId) return null;
      const themeResponse = await companyApi.getTheme(companyId);
      if (!themeResponse.success || !themeResponse.data?.theme?.logoUrl) {
        return null;
      }
      const downloadResponse = await companyApi.getLogoDownloadUrl(companyId);
      if (!downloadResponse.success || !downloadResponse.data?.url) {
        return null;
      }
      return downloadResponse.data.url as string;
    },
    enabled: enabled && !!companyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Hook to fetch default theme settings
 *
 * @returns React Query result with default themes
 */
export const useDefaultThemes = (): UseQueryResult<ApiResponse<{
  lightTheme: CompanyThemeSettings['lightTheme'];
  darkTheme: CompanyThemeSettings['darkTheme'];
}>, Error> => {
  return useQuery({
    queryKey: queryKeys.companyTheme.defaults(),
    queryFn: async () => {
      const response = await companyApi.getDefaultThemes();

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch default themes');
      }

      return response;
    },
    staleTime: 30 * 60 * 1000,       // 30 minutes - defaults rarely change
    gcTime: 60 * 60 * 1000,          // 60 minutes
    retry: 1,
  });
};
